<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TeamAssignmentAPI from 'dashboard/api/teamAssignment';
import TeamsAPI from 'dashboard/api/teams';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import AgentSelector from '../AgentSelector.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const teamId = route.params.teamId;
const isSaving = ref(false);
const isLoading = ref(true);
const teamMembers = ref([]);
const selectedAgentIds = ref([]);

const config = ref({
  method: 'manual',
  require_online: true,
});

const isAutomatic = computed(() => config.value.method === 'round_robin');

const updateSelectedAgents = ids => {
  selectedAgentIds.value = [...ids];
};

const loadTeamMembers = async () => {
  try {
    const { data: membersData } = await TeamsAPI.getAgents({ teamId });
    teamMembers.value = membersData || [];
  } catch (_e) {
    teamMembers.value = [];
  }
};

const load = async () => {
  isLoading.value = true;
  try {
    const { data: configData } = await TeamAssignmentAPI.getConfig(teamId);
    const saved = configData.assignment_config || {};
    config.value = {
      method: saved.method || 'manual',
      require_online: saved.require_online !== false,
    };
    await loadTeamMembers();
    const memberIds = teamMembers.value.map(agent => agent.id);
    const savedIds = (saved.agent_ids || [])
      .map(Number)
      .filter(id => memberIds.includes(id));
    selectedAgentIds.value = savedIds.length ? savedIds : memberIds;
  } catch (_e) {
    useAlert(t('TEAMS_SETTINGS.ASSIGNMENT.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  if (isAutomatic.value && !selectedAgentIds.value.length) {
    useAlert(t('TEAMS_SETTINGS.ASSIGNMENT.AGENT_VALIDATION_ERROR'));
    return;
  }

  isSaving.value = true;
  try {
    const payload = {
      method: config.value.method,
      enabled: config.value.method === 'round_robin',
      require_online: config.value.require_online,
      agent_ids: isAutomatic.value ? selectedAgentIds.value : [],
    };
    await TeamAssignmentAPI.updateConfig(teamId, payload);
    useAlert(t('TEAMS_SETTINGS.ASSIGNMENT.SAVE_SUCCESS'));
    const finishRoute =
      route.name === 'settings_teams_add_assignment'
        ? 'settings_teams_finish'
        : 'settings_teams_edit_finish';
    router.push({ name: finishRoute, params: route.params });
  } catch (_e) {
    useAlert(t('TEAMS_SETTINGS.ASSIGNMENT.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="col-span-6 p-6 flex flex-col gap-6 max-w-3xl">
    <div>
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('TEAMS_SETTINGS.ASSIGNMENT.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ $t('TEAMS_SETTINGS.ASSIGNMENT.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-11">
      {{ $t('TEAMS_SETTINGS.ASSIGNMENT.LOADING') }}
    </div>

    <template v-else>
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TEAMS_SETTINGS.ASSIGNMENT.METHOD') }}
        </label>
        <div class="flex flex-col gap-2">
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              v-model="config.method"
              type="radio"
              value="manual"
              class="mt-1"
            />
            <span>
              <span class="text-sm text-n-slate-12 block">
                {{ $t('TEAMS_SETTINGS.ASSIGNMENT.METHODS.MANUAL') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ $t('TEAMS_SETTINGS.ASSIGNMENT.METHODS.MANUAL_HELP') }}
              </span>
            </span>
          </label>
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              v-model="config.method"
              type="radio"
              value="round_robin"
              class="mt-1"
            />
            <span>
              <span class="text-sm text-n-slate-12 block">
                {{ $t('TEAMS_SETTINGS.ASSIGNMENT.METHODS.ROUND_ROBIN') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ $t('TEAMS_SETTINGS.ASSIGNMENT.METHODS.ROUND_ROBIN_HELP') }}
              </span>
            </span>
          </label>
        </div>
      </div>

      <template v-if="isAutomatic">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.ASSIGNMENT.REQUIRE_ONLINE') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('TEAMS_SETTINGS.ASSIGNMENT.REQUIRE_ONLINE_HELP') }}
            </p>
          </div>
          <Switch v-model="config.require_online" />
        </div>

        <div class="flex flex-col gap-2">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.ASSIGNMENT.SELECT_AGENTS') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('TEAMS_SETTINGS.ASSIGNMENT.SELECT_AGENTS_HELP') }}
            </p>
          </div>
          <AgentSelector
            v-if="teamMembers.length"
            :agent-list="teamMembers"
            :selected-agents="selectedAgentIds"
            :update-selected-agents="updateSelectedAgents"
            :is-working="isSaving"
          />
          <p v-else class="text-xs text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.ASSIGNMENT.AGENT_VALIDATION_ERROR') }}
          </p>
        </div>
      </template>

      <div class="flex justify-end gap-2 pt-2">
        <NextButton
          :label="$t('TEAMS_SETTINGS.ASSIGNMENT.SAVE')"
          :is-loading="isSaving"
          @click="save"
        />
      </div>
    </template>
  </div>
</template>
